/**
 * アンケートの目盛バー（エアコンの温度スケール型）。
 *
 * 選択肢をボタンとして横に並べるのではなく、**連続した 1 本のバーを段で区切り、
 * 選んだ段まで同系色の濃淡で塗る**。理由は 2 つ:
 *
 * - 貢献度 1〜5 は順序尺度なので「5 が 1 より大きい」ことが形として見えるべきで、
 *   同じ見た目のボタン 5 個では順序が色・面積に現れない
 * - ドラッグで連続的に変えられるので、5 軸 8 問を 1 本の動作で流し込める
 *
 * `offScale`（貢献度の「対象外」）は尺度の外側にある選択肢なので、バーの段にはせず
 * 左の独立したチップにする。バーに混ぜると「0 点」と誤読され、ドラッグの通過点にもなる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business アンケート回答の目盛バー（段階塗り分け + ドラッグ / キーボード操作）
 * @graph-connects none
 */
import { useRef, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent } from 'react';

interface SurveyScaleBarProps<T extends string> {
  /** 目盛（低い順）。この並びで塗り色が濃くなる */
  scale: readonly T[];
  /** 尺度の外側の選択肢（例: 貢献度の「対象外」）。バーの左にチップで出す */
  offScale?: T;
  value: T | null;
  onChange: (next: T) => void;
  /** radiogroup のラベル（軸名） */
  ariaLabel: string;
  /** 軸の代表色（ID 看板と同じ色）。段の濃淡はこの色で作る */
  color: string;
}

/** 選択済みの段の塗り濃度（最も低い段）。 @graph-connects none */
const FILL_MIN_PERCENT = 46;
/** 選択済みの段の塗り濃度（最も高い段）。 @graph-connects none */
const FILL_MAX_PERCENT = 96;
/** 未選択の段の塗り濃度。バーの全長が見える程度に薄く残す。 @graph-connects none */
const FILL_EMPTY_PERCENT = 6;

/**
 * 段の背景色。選択値までを濃淡で塗り、それより上は薄い地のまま残す。
 * `transparent` と混ぜるのでライト / ダークどちらのテーマでも地の色に乗る。
 * @graph-connects none
 */
function segmentFill(color: string, index: number, count: number, selectedIndex: number): string {
  if (index > selectedIndex) {
    return `color-mix(in srgb, ${color} ${String(FILL_EMPTY_PERCENT)}%, transparent)`;
  }
  const ratio = count <= 1 ? 1 : index / (count - 1);
  const percent = Math.round(FILL_MIN_PERCENT + (FILL_MAX_PERCENT - FILL_MIN_PERCENT) * ratio);
  return `color-mix(in srgb, ${color} ${String(percent)}%, transparent)`;
}

/** 段の角丸（バーの両端だけ丸める）。 @graph-connects none */
function segmentRadius(index: number, count: number): string {
  const head = index === 0 ? 'rounded-l-md' : '';
  const tail = index === count - 1 ? 'rounded-r-md' : '';
  return `${head} ${tail}`;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 目盛バー本体。クリック / ドラッグ / 矢印キーで値を選ぶ
 * @graph-connects none
 */
export function SurveyScaleBar<T extends string>({
  scale,
  offScale,
  value,
  onChange,
  ariaLabel,
  color,
}: SurveyScaleBarProps<T>) {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const selectedIndex = value == null ? -1 : scale.indexOf(value);

  /** 座標 → 段の index。バーの外へはみ出しても両端に丸める。 */
  const pickAt = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (rect == null || rect.width === 0) return;
    const ratio = (clientX - rect.left) / rect.width;
    const index = Math.min(scale.length - 1, Math.max(0, Math.floor(ratio * scale.length)));
    const next = scale[index];
    if (next !== value) onChange(next);
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    dragging.current = true;
    // ポインタを捕捉してバーの外までドラッグを追従させる（jsdom には無いので存在確認する）
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pickAt(event.clientX);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    pickAt(event.clientX);
  };

  const stopDragging = () => {
    dragging.current = false;
  };

  /** 矢印キーで 1 段ずつ移動する（radiogroup の作法）。 */
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const delta = event.key === 'ArrowRight' || event.key === 'ArrowUp' ? 1 : 0;
    const back = event.key === 'ArrowLeft' || event.key === 'ArrowDown' ? -1 : 0;
    if (delta === 0 && back === 0) return;
    event.preventDefault();
    const base = selectedIndex < 0 ? 0 : selectedIndex + delta + back;
    onChange(scale[Math.min(scale.length - 1, Math.max(0, base))]);
  };

  return (
    <div className="flex items-center gap-2" role="radiogroup" aria-label={ariaLabel}>
      {offScale !== undefined && (
        <button
          type="button"
          role="radio"
          aria-checked={value === offScale}
          onClick={() => {
            onChange(offScale);
          }}
          className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] whitespace-nowrap transition-colors ${
            value === offScale
              ? 'border-[var(--color-text-secondary)] bg-[var(--color-bg-hover)] text-[var(--color-text-primary)]'
              : 'border-[var(--color-border)] text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)]'
          }`}
        >
          {offScale}
        </button>
      )}
      <div
        ref={trackRef}
        className="flex flex-1 touch-none gap-px select-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
        onKeyDown={handleKeyDown}
      >
        {scale.map((option, index) => {
          const selected = value === option;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => {
                onChange(option);
              }}
              className="flex flex-1 cursor-pointer flex-col items-center gap-0.5"
            >
              <span
                className={`block h-6 w-full ${segmentRadius(index, scale.length)}`}
                style={{
                  background: segmentFill(color, index, scale.length, selectedIndex),
                  boxShadow: selected ? `inset 0 0 0 2px ${color}` : undefined,
                }}
              />
              <span
                className="text-[11px] leading-none"
                style={{
                  color: selected ? color : 'var(--color-text-muted)',
                  fontWeight: selected ? 600 : 400,
                }}
              >
                {option}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
